import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { Pagination } from "antd";
import { NotificationsSentSkeleton } from "../../components/states";
import NotificationDetailModal from "../../components/NotificationDetailModal/NotificationDetailModal";
import EditScheduledNotificationModal from "../../components/ScheduledNotification/EditScheduledNotificationModal";
import CancelScheduledNotificationModal from "../../components/ScheduledNotification/CancelScheduledNotificationModal";
import { GET_SENT_NOTIFICATIONS } from "../../graphql/notificationHistoryQueries";

const DAYS_BACK = 30;
const PAGE_SIZE = 10;

const TYPE_LABEL = {
  ADMIN_BROADCAST: "Broadcast",
  SYSTEM: "System",
};

const dateLabel = (raw) => {
  const d = new Date(+raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatScheduled = (raw) => {
  const d = new Date(+raw);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

// Full recipient string (all names) — used in the popup and as a hover title.
const recipientFull = (n) =>
  n.recipientNames && n.recipientNames.length
    ? n.recipientNames.join(", ")
    : n.recipientLabel;

// Compact recipient for the table cell: up to 2 names, then "+N".
const recipientShort = (n) => {
  const names = n.recipientNames || [];
  if (names.length === 0) return n.recipientLabel;
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
};

// Minor label clarifying what kind of recipients these are (singular/plural by
// how many were selected). Bulk "All …" sends already read clearly, so no tag.
const KIND_LABEL = {
  USER: ["User", "Users"],
  AGENT: ["Agent", "Agents"],
  COMPANY: ["Company", "Companies"],
  OFFICE: ["Office", "Offices"],
};
const recipientKind = (n) => {
  if (n.recipientType === "EVERYONE") return "All users";
  if (n.recipientType === "ALL_OFFICES") return "All offices";
  const pair = KIND_LABEL[n.recipientType];
  if (!pair) return null;
  return (n.recipientNames || []).length === 1 ? pair[0] : pair[1];
};

// Status word + colors derived from the per-recipient counts, plus the data for
// a mini delivered/failed bar. "Delivered" only when every recipient confirmed;
// any failures → Partially delivered; none → Failed; still confirming → Sent.
const STATUS_TONE = {
  Delivered: { text: "text-green", dot: "bg-green" },
  "Partially delivered": { text: "text-amber-600", dot: "bg-amber-500" },
  Failed: { text: "text-red", dot: "bg-red" },
  Sent: { text: "text-gray-500", dot: "bg-gray-400" },
  "No Recipients": { text: "text-gray-400", dot: "bg-gray-300" },
  Scheduled: { text: "text-blue", dot: "bg-blue" },
};
const delivery = (n) => {
  const total = n.recipientCount || 0;
  const dd = n.deliveredCount || 0;
  const ff = n.failedCount || 0;
  const pp = n.pendingCount || 0;
  const tracked = dd + ff + pp > 0;
  let word;
  if (!tracked) word = n.status || "Delivered"; // legacy rows without tracking
  else if (pp > 0) word = "Sent";
  else if (ff === 0) word = "Delivered";
  else if (dd === 0) word = "Failed";
  else word = "Partially delivered";
  const tone = STATUS_TONE[word] || STATUS_TONE.Sent;
  return {
    word,
    text: tone.text,
    dot: tone.dot,
    showBar: tracked && total > 1,
    delivered: dd,
    total,
    pctDelivered: total ? (dd / total) * 100 : 0,
    pctFailed: total ? (ff / total) * 100 : 0,
  };
};

// Popup recipient: up to 20 names, then "+N".
const MODAL_NAME_CAP = 20;
const recipientModal = (n) => {
  const names = n.recipientNames || [];
  if (names.length === 0) return n.recipientLabel;
  if (names.length <= MODAL_NAME_CAP) return names.join(", ");
  return `${names.slice(0, MODAL_NAME_CAP).join(", ")} +${
    names.length - MODAL_NAME_CAP
  }`;
};

const SentNotificationsTable = () => {
  const { companyId } = useParams();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [canceling, setCanceling] = useState(null);

  const { data, loading } = useQuery(GET_SENT_NOTIFICATIONS, {
    variables: { companyId: companyId || null, daysBack: DAYS_BACK },
    fetchPolicy: "network-only",
  });

  const sent = useMemo(() => data?.getSentNotifications || [], [data]);
  // Refetches can shrink the list; clamp so we never sit on an empty page.
  const safePage = Math.min(page, Math.max(1, Math.ceil(sent.length / PAGE_SIZE)));
  const visible = useMemo(
    () => sent.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [sent, safePage]
  );

  const handleClose = useCallback(() => setSelected(null), []);

  if (loading && !sent.length) return <NotificationsSentSkeleton />;

  if (sent.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
        <h3 className="text-base font-semibold text-gray-700 m-0">
          No sent notifications
        </h3>
        <p className="text-sm text-gray-500 mt-1 m-0">
          Notifications you send will appear here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Recipient</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((n) => {
              const d = delivery(n);
              const isScheduled = d.word === "Scheduled";
              return (
              <tr key={n._id} className="hover:bg-gray-50">
                <td className="px-5 py-4 text-secondary whitespace-nowrap">
                  {dateLabel(n.createdAt)}
                </td>
                <td className="px-5 py-4">
                  <span className="inline-block text-[11px] font-bold uppercase tracking-wider text-secondary bg-secondary/10 px-2 py-1 rounded-full">
                    {TYPE_LABEL[n.type] || n.type || "Broadcast"}
                  </span>
                </td>
                <td
                  className="px-5 py-4 max-w-[260px]"
                  title={recipientFull(n)}
                >
                  {recipientKind(n) && (
                    <span className="inline-block text-[9px] font-semibold uppercase tracking-wider text-white bg-primary_dark px-1 py-0.5 rounded-full mb-1 leading-tight">
                      {recipientKind(n)}
                    </span>
                  )}
                  <div className="font-medium text-gray-900 truncate">
                    {recipientShort(n)}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap ${d.text}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${d.dot}`} />
                    {d.word}
                  </span>
                  {isScheduled && (
                    <div className="text-xs text-gray-400 mt-1">
                      Fires {formatScheduled(n.scheduledAt)}
                    </div>
                  )}
                  {d.showBar && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex h-1.5 w-14 rounded-full overflow-hidden bg-gray-200">
                        <span
                          className="h-full bg-green shrink-0"
                          style={{ width: `${d.pctDelivered}%` }}
                        />
                        <span
                          className="h-full bg-red shrink-0"
                          style={{ width: `${d.pctFailed}%` }}
                        />
                      </span>
                      <span className="text-xs text-gray-400">
                        {d.delivered}/{d.total}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-5 py-4 text-right">
                  {isScheduled ? (
                    <span className="whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => setEditing(n)}
                        className="text-primary_dark font-semibold hover:underline"
                      >
                        Edit
                      </button>
                      <span className="text-gray-300 mx-1.5">·</span>
                      <button
                        type="button"
                        onClick={() => setCanceling(n)}
                        className="text-red font-semibold hover:underline"
                      >
                        Cancel
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSelected(n)}
                      className="text-primary_dark font-semibold hover:underline whitespace-nowrap"
                    >
                      View notification
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex justify-center">
        <Pagination
          current={safePage}
          pageSize={PAGE_SIZE}
          total={sent.length}
          onChange={setPage}
          showSizeChanger={false}
          hideOnSinglePage
        />
      </div>

      {selected && (
        <NotificationDetailModal
          notification={selected}
          recipient={recipientModal(selected)}
          kind={recipientKind(selected)}
          status={delivery(selected).word}
          onClose={handleClose}
        />
      )}
      {editing && (
        <EditScheduledNotificationModal
          notification={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {canceling && (
        <CancelScheduledNotificationModal
          notification={canceling}
          recipient={recipientModal(canceling)}
          onClose={() => setCanceling(null)}
        />
      )}
    </>
  );
};

export default SentNotificationsTable;
